import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, UserCog } from 'lucide-react';
import { appName } from '../config/branding';

const LoginPage = () => {
  const { login, loginWithChooser } = useAuth();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <a href="/" className="inline-block mb-6">
            <svg width="200" height="36" viewBox="0 0 200 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <text x="0" y="30" fontFamily="Inter, sans-serif" fontSize="26" fontWeight="800" fill="#37b34a">{appName}</text>
            </svg>
          </a>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome back
          </h1>
          <p className="text-gray-500">
            Sign in to manage your service requests and connect with professionals.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <button
            data-testid="login-google-btn"
            onClick={login}
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 hover:border-gray-300 rounded-xl py-3.5 px-4 text-gray-700 font-medium transition-colors duration-200"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div
            data-testid="login-account-chooser-block"
            className="mt-6 pt-5 border-t border-gray-100 text-center"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
              Wrong account? Or Signed into another account?
            </p>
            <button
              data-testid="login-choose-another-account-btn"
              onClick={loginWithChooser}
              className="inline-flex items-center justify-center gap-2 text-[#37b34a] hover:text-[#2d9a3e] text-sm font-semibold px-4 py-2 rounded-lg border border-[#37b34a]/40 hover:border-[#37b34a] hover:bg-green-50/50 transition-colors"
            >
              <UserCog size={16} />
              Choose another Google account
            </button>

            <details className="mt-4 text-left text-xs text-gray-500 group">
              <summary
                data-testid="login-switch-help-toggle"
                className="cursor-pointer text-center font-medium text-gray-600 hover:text-gray-900 group-open:mb-2"
              >
                Still signing you into the wrong account?
              </summary>
              <ol className="list-decimal pl-4 space-y-1 leading-relaxed">
                <li>
                  Open{' '}
                  <a
                    href="https://myaccount.google.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#37b34a] hover:underline"
                  >
                    myaccount.google.com
                  </a>{' '}
                  in a new tab.
                </li>
                <li>
                  Click your profile photo (top right) and{' '}
                  <span className="font-semibold">sign out</span> of the account
                  you don&apos;t want to use here.
                </li>
                <li>Come back to this tab and click <span className="font-semibold">Continue with Google</span> again.</li>
              </ol>
              <p className="mt-2 text-[11px] text-gray-400">
                Your other Google services (Gmail, Drive, YouTube) stay signed in on any
                accounts you didn&apos;t sign out of.
              </p>
            </details>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              By signing in, you agree to our{' '}
              <a href="/terms" className="text-[#37b34a] hover:underline">Terms of Service</a>{' '}and{' '}
              <a href="/privacy" className="text-[#37b34a] hover:underline">Privacy Policy</a>.
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <a
            href="/"
            className="text-[#37b34a] hover:text-[#2d9a3e] text-sm font-medium inline-flex items-center gap-1 transition-colors"
          >
            Back to home
            <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
